import React, { useState, useEffect, useCallback } from 'react';
import { Card, message, Tooltip } from 'antd';
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import { Kanban } from '../Kanban/Kanban.jsx';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAllowedFunnelStatuses, clearAllowedFunnelStatuses } from '../../redux/vacancyCandidatesSlice';
import { STATUSES } from '../../constants/candidateColumnLabels';
import CompletionModalKanban from './CompletionModalKanban';
import {
  canEditCandidate as canEditCandidatePerm,
  canChangeCandidateStatus
} from '../../utils/candidatePermissions';

const CandidateKanban = ({
  candidates,
  vacancies,
  onStatusChange,
  onUpdate,
  onDelete,
  onView,
  organisationId,
  onCandidatesDataReady,
  selectedStatuses = []
}) => {
  const dispatch = useDispatch();
  const { funnelStatuses } = useSelector((state) => state.vacancies);
  const { allowedFunnelStatuses } = useSelector((state) => state.vacancyCandidates);
  const { role: roleFromStore } = useSelector((state) => state.auth);
  const storedRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const effectiveRole = roleFromStore || storedRole;
  const canChangeStatus = canChangeCandidateStatus(effectiveRole);
  const canEditCandidate = canEditCandidatePerm(effectiveRole).canEdit;

  const [candidatesVacanciesArray, setCandidatesVacanciesArray] = useState([]);
  const [loading, setLoading] = useState(false);
  const [completionModalVisible, setCompletionModalVisible] = useState(false);
  const [completionType, setCompletionType] = useState(null);
  const [pendingCompletionItem, setPendingCompletionItem] = useState(null);
  const [resetDraggingTrigger, setResetDraggingTrigger] = useState(null);

  const fetchVacancyDataForCandidates = useCallback(async () => {
    if (!candidates || candidates.length === 0) return;

    setLoading(true);
    try {
      const candidatesVacanciesData = [];

      candidates.forEach((candidate) => {
        if (candidate.vacancies && Array.isArray(candidate.vacancies)) {
          candidate.vacancies.forEach(vacancy => {
            const fullVacancyInfo = vacancies?.find(v => v.id === vacancy.vacancy_id) || {};

            candidatesVacanciesData.push({
              id: `${candidate.id}-${vacancy.vacancy_id || vacancy.id}`,
              candidate: { ...candidate, status: vacancy?.status_id?.toString() || '1' },
              vacancyInfo: { ...fullVacancyInfo, ...vacancy },
              status: vacancy?.status_id?.toString() || '1'
            });
          });
        }
      });

      setCandidatesVacanciesArray(candidatesVacanciesData);

      if (onCandidatesDataReady) {
        onCandidatesDataReady(candidatesVacanciesData);
      }
    } catch (error) {
      console.error('Error loading vacancy data:', error);
    } finally {
      setLoading(false);
    }
  }, [candidates, onCandidatesDataReady, vacancies]);

  useEffect(() => {
    fetchVacancyDataForCandidates();
  }, [fetchVacancyDataForCandidates]);

  const getStatuses = useCallback(() => {
    if (!funnelStatuses || funnelStatuses.length === 0) {
      return {
        '1': { title: 'Новые', color: STATUSES.new.color },
        '2': { title: 'В работе', color: STATUSES.interview.color },
        '3': { title: 'Завершено', color: STATUSES.hired.color }
      };
    }

    const statuses = {};
    funnelStatuses.forEach(status => {
      const isAllowed = allowedFunnelStatuses && allowedFunnelStatuses.length > 0
        ? allowedFunnelStatuses.some(allowed =>
          allowed.id === status.id || allowed.status_id === status.id
        )
        : true;

      const isSelected = selectedStatuses.length === 0 || selectedStatuses.includes(status.id.toString());

      if (isSelected) {
        const statusKey = getStatusKeyFromName(status.name);
        const statusColor = statusKey && STATUSES[statusKey] ? STATUSES[statusKey].color : '#4a90e2';

        statuses[status.id.toString()] = {
          title: status.name,
          color: statusColor,
          disabled: (!canChangeStatus) || (allowedFunnelStatuses && allowedFunnelStatuses.length > 0 ? !isAllowed : false)
        };
      }
    });
    return statuses;
  }, [funnelStatuses, allowedFunnelStatuses, selectedStatuses, canChangeStatus]);

  const getStatusKeyFromName = (statusName) => {
    if (statusName.includes('Новый')) return 'new';
    if (statusName.includes('Перезвонить')) return 'call_back';
    if (statusName.includes('Собеседование')) return 'interview';
    if (statusName.includes('Сбор рекомендаций')) return 'references';
    if (statusName.includes('Предложение о работе')) return 'offer';
    if (statusName.includes('Оформление документов')) return 'documentation';
    if (statusName.includes('Трудоустроен') || statusName.includes('Завершенный')) return 'hired';
    if (statusName.includes('Отклонён') || statusName.includes('Отказ')) return 'failed';
    return null;
  };

  const getVacancyName = (vacancyInfo) => {
    if (!vacancyInfo) return 'Вакансия не указана';
    return vacancyInfo.job_title || vacancyInfo.job_title || `Вакансия ${vacancyInfo.job_title}`;
  };

  const renderCard = (item) => {
    const { candidate, vacancyInfo } = item;

    return (
      <Card
        key={`${candidate.id}-${vacancyInfo?.id || vacancyInfo?.vacancy_id}`}
        size="small"
        style={{ marginBottom: '8px' }}
        actions={[
          <Tooltip key="view" title="Просмотреть кандидата">
            <EyeOutlined onClick={() => onView(candidate)} />
          </Tooltip>,
          <Tooltip key="edit" title="Редактировать кандидата">
            <EditOutlined disabled={!canEditCandidate} onClick={() => onUpdate(candidate)} />
          </Tooltip>
          // <Tooltip title="Удалить кандидата">
          //   <DeleteOutlined key="delete" onClick={() => onDelete(candidate.id)} />
          // </Tooltip>,
        ].filter(Boolean)}
      >

        <p>ID кандидата: {candidate.id}</p>
        <p>Кандидат: {candidate.full_name}</p>
        <p>Телефон: {candidate.phone_number || 'Не указан'}</p>
        <p>Вакансия: {getVacancyName(vacancyInfo)}</p>
        <p>Зарплата: {candidate.salary_range || 'Не указана'}</p>
        {candidate.files && candidate.files.length > 0 && (
          <p>Резюме: {candidate.files.length} файл(ов)</p>
        )}
      </Card>
    );
  };

  const handleDragStart = async (item) => {
    try {
      if (!canChangeStatus) {
        message.error('Нет прав для изменения статусов');
        return;
      }
      const { vacancyInfo } = item;
      if (!vacancyInfo) return;

      const vacancyId = vacancyInfo.vacancy_id || vacancyInfo.id;
      if (vacancyId) {
        await dispatch(fetchAllowedFunnelStatuses(vacancyId)).unwrap();
      }
    } catch (error) {
      console.error('Error fetching allowed funnel statuses:', error);
    }
  };

  const handleDragEnd = async (item, newStatus, newIndex) => {
    try {
      if (!canChangeStatus) {
        message.error('Нет прав для изменения статусов');
        return;
      }
      const { candidate, vacancyInfo } = item;
      if (!candidate || !vacancyInfo) return;

      if (newStatus === 'completion-success' || newStatus === 'completion-failure' || newStatus === 'completion-reserve') {
        const type = newStatus.replace('completion-', '');
        setCompletionType(type);
        setPendingCompletionItem(item);
        setCompletionModalVisible(true);
        dispatch(clearAllowedFunnelStatuses());
        return;
      }

      if (onStatusChange) {
        await onStatusChange(item, parseInt(newStatus), newIndex);
      }

      dispatch(clearAllowedFunnelStatuses());

    } catch (error) {
      console.error('Error updating candidate status:', error);
    }
  };

  const handleCompletionConfirm = async (completionData) => {
    if (!pendingCompletionItem) {
      console.error('No pending completion item');
      return;
    }

    setLoading(true);
    try {
      let targetStatusId = null;

      if (completionType === 'success') {
        const successStatus = funnelStatuses?.find(s =>
          s.name.includes('Трудоустроен') || s.name.includes('Завершить')
        );
        targetStatusId = successStatus?.id;
      } else if (completionType === 'failure') {
        const failureStatus = funnelStatuses?.find(s =>
          s.name.includes('Завершить') || s.name.includes('Отказ')
        );
        targetStatusId = failureStatus?.id;
      } else if (completionType === 'reserve') {
        const reserveStatus = funnelStatuses?.find(s => s.id === 11);
        targetStatusId = reserveStatus?.id;
      }

      if (!targetStatusId) {
        message.error('Не удалось найти соответствующий статус завершения');
        setLoading(false);
        return;
      }

      if (onStatusChange) {
        await onStatusChange(pendingCompletionItem, targetStatusId, 0, completionData);
      } else {
        console.error('onStatusChange is not defined');
      }

      setCompletionModalVisible(false);
      setCompletionType(null);
      setPendingCompletionItem(null);
      setResetDraggingTrigger(Date.now()); // Сбрасываем состояние перетаскивания
    } catch (error) {
      console.error('Error completing candidate:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompletionCancel = () => {
    setCompletionModalVisible(false);
    setCompletionType(null);
    setPendingCompletionItem(null);
    dispatch(clearAllowedFunnelStatuses());
    setResetDraggingTrigger(Date.now()); // Сбрасываем состояние перетаскивания
  };

  const handleDragCancel = async (item) => {
    try {
      dispatch(clearAllowedFunnelStatuses());
    } catch (error) {
      console.error('Error clearing allowed funnel statuses:', error);
    }
  };

  if (!funnelStatuses || funnelStatuses.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
        Воронка не настроена для этой вакансии
      </div>
    );
  }

  const activeStatuses = Object.values(getStatuses()).filter(status => !status.disabled);
  // if (!canChangeStatus) {
  //   return (
  //     <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
  //       Нет прав для изменения статусов
  //     </div>
  //   );
  // }
  if (activeStatuses.length === 0 && allowedFunnelStatuses && allowedFunnelStatuses.length > 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
        Нет доступных статусов для перетаскивания
      </div>
    );
  }

  return (
    <>
      <Kanban
        items={candidatesVacanciesArray}
        columns={getStatuses()}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        renderCard={renderCard}
        resetDraggingTrigger={resetDraggingTrigger}
        completionZones={{
          success: { enabled: true },
          failure: { enabled: true },
          reserve: { enabled: true }
        }}

      />

      <CompletionModalKanban
        visible={completionModalVisible}
        completionType={completionType}
        onCancel={handleCompletionCancel}
        onConfirm={handleCompletionConfirm}
        loading={loading}
      />
    </>
  );
};

export default CandidateKanban;  